import { adminApiClient } from '@/services/admin/baseService'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { buildModeAwareConfig } from '@/services/admin/modeAware'
import {
  normalizeAnnouncementBarContent,
  normalizeAnnouncementChannels,
} from '@/services/admin/announcementContent'
import type {
  ApiResponse,
  Announcement,
  AnnouncementDetail,
  AnnouncementChannels,
  BarContentSegment,
} from '@/types'

export interface AnnouncementListParams {
  status?: string
  limit?: number
  offset?: number
  page?: number
}

export interface AnnouncementListResult {
  items: Announcement[]
  total: number
}

const mapAnnouncement = (record: any): Announcement => ({
  id: record?.id ?? '',
  title: record?.title ?? '',
  emailSubtitle: record?.email_subtitle ?? undefined,
  content: record?.content ?? '',
  barContent:
    (normalizeAnnouncementBarContent(record?.barContent ?? record?.bar_content) as
      | BarContentSegment[]
      | undefined),
  severity: record?.severity ?? 'notice',
  channels: normalizeAnnouncementChannels(record?.channels) as AnnouncementChannels,
  status: record?.status ?? 'draft',
  startsAt: record?.startsAt ?? record?.starts_at ?? undefined,
  endsAt: record?.endsAt ?? record?.ends_at ?? undefined,
  ctaLabel: record?.ctaLabel ?? record?.cta_label ?? undefined,
  ctaUrl: record?.ctaUrl ?? record?.cta_url ?? undefined,
  emailTemplateKey: record?.emailTemplateKey ?? record?.email_template_key ?? undefined,
  emailContentBlocks: record?.emailContentBlocks ?? record?.email_content_blocks ?? undefined,
  version: Number(record?.version ?? 0),
  createdBy: record?.createdBy ?? record?.created_by ?? '',
  createdAt: record?.createdAt ?? record?.created_at ?? '',
  updatedAt: record?.updatedAt ?? record?.updated_at ?? '',
  publishedAt: record?.publishedAt ?? record?.published_at ?? undefined,
  revokedAt: record?.revokedAt ?? record?.revoked_at ?? undefined,
})

const mapAnnouncementDetail = (record: any): AnnouncementDetail => ({
  ...mapAnnouncement(record),
  deliveries: Array.isArray(record?.deliveries)
    ? record.deliveries.map((delivery: any) => ({
        channel: delivery?.channel ?? 'modal',
        status: delivery?.status ?? 'delivered',
        deliveredAt: delivery?.deliveredAt ?? delivery?.delivered_at ?? '',
        error: delivery?.error ?? null,
        metadata: delivery?.metadata ?? null,
        updatedAt: delivery?.updatedAt ?? delivery?.updated_at ?? '',
      }))
    : [],
})

export const announcementService = {
  async listAnnouncements(
    params?: AnnouncementListParams,
    mode?: NetworkEnvironment
  ): Promise<AnnouncementListResult> {
    const searchParams = new URLSearchParams()
    const limit = params?.limit ?? 50
    const offset =
      params?.offset ??
      (params?.page && params?.page > 0 ? (params.page - 1) * limit : 0)

    if (params?.status) {
      searchParams.append('status', params.status)
    }
    searchParams.append('limit', limit.toString())
    searchParams.append('offset', offset.toString())

    const query = searchParams.toString()
    const url = query ? `/admin/announcements?${query}` : '/admin/announcements'
    const response = await adminApiClient.get<ApiResponse<{ items: any[]; total: number }>>(
      url,
      buildModeAwareConfig(mode)
    )

    const rawItems = response.data?.data?.items ?? []
    const items = rawItems.map(mapAnnouncement)
    const total = Number(response.data?.data?.total ?? items.length)

    return { items, total }
  },

  async getAnnouncement(id: string, mode?: NetworkEnvironment): Promise<AnnouncementDetail> {
    const response = await adminApiClient.get<ApiResponse<any>>(
      `/admin/announcements/${id}`,
      buildModeAwareConfig(mode)
    )
    return mapAnnouncementDetail(response.data?.data ?? {})
  },

  async createAnnouncement(payload: Record<string, unknown>): Promise<Announcement> {
    const response = await adminApiClient.post<ApiResponse<any>>('/admin/announcements', payload)
    return mapAnnouncement(response.data?.data ?? {})
  },

  async updateAnnouncement(id: string, payload: Record<string, unknown>): Promise<Announcement> {
    const response = await adminApiClient.put<ApiResponse<any>>(
      `/admin/announcements/${id}`,
      payload
    )
    return mapAnnouncement(response.data?.data ?? {})
  },

  async publishAnnouncement(id: string, payload: Record<string, unknown>): Promise<Announcement> {
    const response = await adminApiClient.post<ApiResponse<any>>(
      `/admin/announcements/${id}/publish`,
      payload
    )
    return mapAnnouncement(response.data?.data ?? {})
  },

  async revokeAnnouncement(id: string): Promise<Announcement> {
    const response = await adminApiClient.post<ApiResponse<any>>(
      `/admin/announcements/${id}/revoke`,
      {}
    )
    return mapAnnouncement(response.data?.data ?? {})
  },
}
